import { useCallback, useState } from "react";
import { Sparkles, X } from "lucide-react";
import AvatarGrid from "../AvatarGrid";

type VirtualAvatarModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: { name: string; avatarId: string }) => Promise<void>;
};

const VirtualAvatarModal: React.FC<VirtualAvatarModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const [selectedAvatarId, setSelectedAvatarId] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setSelectedAvatarId("");
    setName("");
    setError(null);
    setIsSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    resetState();
    onClose();
  }, [isSubmitting, onClose, resetState]);

  const handleConfirm = useCallback(async () => {
    if (!selectedAvatarId) {
      setError("Selecione um avatar para adicionar ao cenário.");
      return;
    }

    if ((!name || name.trim() === "")) {
      setError("Por favor, insira um nome para o avatar.");
      return;
    }

    const finalName = name.trim();

    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm({ name: finalName, avatarId: selectedAvatarId });
      resetState();
    } catch (confirmationError) {
      console.error("Erro ao confirmar avatar virtual:", confirmationError);
      setError("Não foi possível adicionar o avatar. Tente novamente.");
      setIsSubmitting(false);
    }
  }, [name, onConfirm, resetState, selectedAvatarId]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-black/60">
      <div className="flex min-h-full items-center justify-center px-4 py-6">
        <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed"
            aria-label="Fechar"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex max-h-[calc(100vh-3rem)] flex-col overflow-y-auto">
            <div className="flex flex-col gap-6 p-6 sm:p-8">
              <header className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600/10 text-blue-600">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">Adicionar avatar ao cenário</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Escolha um avatar adicional para representar elementos da constelação. Apenas o moderador pode manipulá-lo.
                  </p>
                </div>
              </header>

              <div className="grid gap-6 sm:grid-cols-[1.5fr_1fr]">
                <div>
                  <h3 className="mb-3 text-sm font-medium text-gray-700 uppercase tracking-wide">Selecione um avatar</h3>
                  <AvatarGrid
                    selectedAvatarId={selectedAvatarId}
                    onSelect={setSelectedAvatarId}
                    disabled={isSubmitting}
                    variant="compact"
                  />
                </div>

                <div className="rounded-xl bg-gray-50 p-4">
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700" htmlFor="virtual-avatar-name">
                        Nome do avatar
                      </label>
                      <input
                        id="virtual-avatar-name"
                        type="text"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="Ex.: Mãe, Medo, Criança interior"
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength={60}
                        disabled={isSubmitting}
                      />
                      <p className="mt-1 text-xs text-gray-500">O nome ajuda a identificar a energia que este avatar representa.</p>
                    </div>

                    {error && (
                      <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        <span>{error}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirm}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            Processando...
                          </span>
                        ) : (
                          <span>Adicionar</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualAvatarModal;
